import { type ExprNode, type FuncName, num, variable, add, mul, pow, func } from './node';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CONSTANTS: Record<string, number> = {
  pi: Math.PI,
  e: Math.E,
  tau: 2 * Math.PI,
  phi: (1 + Math.sqrt(5)) / 2,
};

// ---------------------------------------------------------------------------
// Function name set (full FuncName surface)
// ---------------------------------------------------------------------------

const FUNCTION_NAMES = new Set<FuncName>([
  'sin',
  'cos',
  'tan',
  'asin',
  'acos',
  'atan',
  'sinh',
  'cosh',
  'tanh',
  'sqrt',
  'cbrt',
  'exp',
  'log',
  'ln',
  'log2',
  'log10',
  'abs',
  'sign',
  'floor',
  'ceil',
  'round',
  'min',
  'max',
  'pow',
]);

// ---------------------------------------------------------------------------
// ParseError
// ---------------------------------------------------------------------------

export class ParseError extends Error {
  constructor(
    message: string,
    public readonly position?: number,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

// ---------------------------------------------------------------------------
// ParseResult
// ---------------------------------------------------------------------------

export interface ParseResult {
  node: ExprNode;
  variables: string[];
  source: string;
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type TokenKind = 'number' | 'identifier' | 'op' | 'caret' | 'lparen' | 'rparen' | 'comma' | 'eof';

interface Token {
  kind: TokenKind;
  value: string;
  position: number;
}

class Tokenizer {
  private pos: number = 0;

  constructor(private src: string) {}

  private peek(): string {
    if (this.pos >= this.src.length) return '\0';
    return this.src[this.pos]!;
  }

  private peekNext(): string {
    if (this.pos + 1 >= this.src.length) return '\0';
    return this.src[this.pos + 1]!;
  }

  private advance(): string {
    const ch = this.peek();
    this.pos += 1;
    return ch;
  }

  private skipWhitespace(): void {
    while (/\s/.test(this.peek())) {
      this.advance();
    }
  }

  nextToken(): Token {
    this.skipWhitespace();

    const start = this.pos;
    const ch = this.peek();

    if (ch === '\0') {
      return { kind: 'eof', value: '', position: start };
    }

    if (ch === '(') {
      this.advance();
      return { kind: 'lparen', value: '(', position: start };
    }
    if (ch === ')') {
      this.advance();
      return { kind: 'rparen', value: ')', position: start };
    }
    if (ch === ',') {
      this.advance();
      return { kind: 'comma', value: ',', position: start };
    }
    if (ch === '^') {
      this.advance();
      return { kind: 'caret', value: '^', position: start };
    }

    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      this.advance();
      return { kind: 'op', value: ch, position: start };
    }

    // Numbers (integers and decimals)
    if (/\d/.test(ch)) {
      let numStr = '';
      while (/\d/.test(this.peek())) {
        numStr += this.advance();
      }
      // Decimal part
      if (this.peek() === '.' && /\d/.test(this.peekNext())) {
        numStr += this.advance(); // '.'
        while (/\d/.test(this.peek())) {
          numStr += this.advance();
        }
      }
      return { kind: 'number', value: numStr, position: start };
    }

    // Identifiers
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (/[a-zA-Z0-9_]/.test(this.peek())) {
        ident += this.advance();
      }
      return { kind: 'identifier', value: ident, position: start };
    }

    throw new ParseError(`Unexpected character '${ch}'`, start);
  }
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

class Parser {
  private tokens: Token[];
  private pos: number = 0;

  constructor(src: string) {
    const tokenizer = new Tokenizer(src);
    const tokens: Token[] = [];
    let token = tokenizer.nextToken();
    while (token.kind !== 'eof') {
      tokens.push(token);
      token = tokenizer.nextToken();
    }
    tokens.push(token); // EOF sentinel
    this.tokens = tokens;
  }

  private peek(): Token {
    if (this.pos >= this.tokens.length) {
      return this.tokens[this.tokens.length - 1]!;
    }
    return this.tokens[this.pos]!;
  }

  private peekAhead(n: number): Token {
    const index = this.pos + n;
    if (index >= this.tokens.length) {
      return this.tokens[this.tokens.length - 1]!;
    }
    return this.tokens[index]!;
  }

  private advance(): Token {
    const token = this.peek();
    if (token.kind !== 'eof') {
      this.pos += 1;
    }
    return token;
  }

  private expect(kind: TokenKind): Token {
    const token = this.peek();
    if (token.kind !== kind) {
      throw new ParseError(
        `Expected ${kind}, got '${token.value}' (${token.kind})`,
        token.position,
      );
    }
    return this.advance();
  }

  /** Negate: fold into num for literals, otherwise mul([-1, expr]). */
  private negate(expr: ExprNode): ExprNode {
    if (expr.kind === 'num') {
      return num(-expr.value);
    }
    return mul([num(-1), expr]);
  }

  parse(): ExprNode {
    if (this.peek().kind === 'eof') {
      throw new ParseError('Empty expression', 0);
    }

    const result = this.expr();

    if (this.peek().kind !== 'eof') {
      const t = this.peek();
      throw new ParseError(`Unexpected token '${t.value}' at position ${t.position}`, t.position);
    }

    return result;
  }

  // expr := term (("+" | "-") term)*
  private expr(): ExprNode {
    const terms: ExprNode[] = [];
    terms.push(this.term());

    while (this.peek().kind === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.advance().value;
      const right = this.term();

      if (op === '+') {
        terms.push(right);
      } else {
        // "-" → add negated term
        terms.push(this.negate(right));
      }
    }

    if (terms.length === 1) {
      return terms[0]!;
    }
    return add(terms);
  }

  // term := factor (("*" | "/") factor | implicit_mul factor)*
  private term(): ExprNode {
    const factors: ExprNode[] = [];
    factors.push(this.factor());

    while (true) {
      const current = this.peek();

      if (current.kind === 'op' && (current.value === '*' || current.value === '/')) {
        const op = this.advance().value;
        const right = this.factor();

        if (op === '*') {
          factors.push(right);
        } else {
          // "/" → push pow(right, -1) to implement left-associative division
          factors.push(pow(right, num(-1)));
        }
      } else if (this.canStartAtom(current)) {
        // Implicit multiplication
        const right = this.factor();
        factors.push(right);
      } else {
        break;
      }
    }

    if (factors.length === 1) {
      return factors[0]!;
    }
    return mul(factors);
  }

  // factor := unary "^" factor | unary   (right-associative)
  private factor(): ExprNode {
    const base = this.unary();

    if (this.peek().kind === 'caret') {
      this.advance();
      const exp = this.factor(); // right-recursive for right-assoc
      return pow(base, exp);
    }

    return base;
  }

  // unary := "-" factorAfterUnary | call
  // Unary minus binds LOOSER than "^", so "-x^2" parses as -(x^2):
  // unary() consumes "-" and parses the operand as a full power via
  // factorAfterUnary() (which picks up the trailing "^"), then negate()
  // wraps the whole power → mul([-1, pow(x, 2)]). factor() handles the
  // no-minus path, calling unary() for its base.
  private unary(): ExprNode {
    const current = this.peek();

    if (current.kind === 'op' && current.value === '-') {
      const pos = current.position;
      this.advance(); // consume '-'

      const next = this.peek();
      if (!this.canStartUnary(next)) {
        throw new ParseError(`Expected expression after unary '-'`, pos);
      }

      // Parse the operand as a full power so "^" binds inside the negation.
      const operand = this.factorAfterUnary();
      return this.negate(operand);
    }

    return this.call();
  }

  /**
   * factorAfterUnary: parses a factor but is only called from inside
   * unary's recursive descent. Handles "-x^2" correctly:
   * unary sees "-", calls factorAfterUnary which parses "x^2" as pow(x,2),
   * then negate wraps the whole thing: mul([-1, pow(x,2)]).
   */
  private factorAfterUnary(): ExprNode {
    const base = this.call();
    if (this.peek().kind === 'caret') {
      this.advance();
      const exp = this.factor(); // right-associative
      return pow(base, exp);
    }
    return base;
  }

  private canStartUnary(token: Token): boolean {
    return (
      token.kind === 'number' ||
      token.kind === 'identifier' ||
      token.kind === 'lparen' ||
      (token.kind === 'op' && token.value === '-')
    );
  }

  // call := identifier "(" args ")" | atom
  private call(): ExprNode {
    if (this.peek().kind === 'identifier' && this.peekAhead(1).kind === 'lparen') {
      const ident = this.peek().value;

      if (FUNCTION_NAMES.has(ident as FuncName)) {
        this.advance(); // consume identifier
        this.advance(); // consume '('

        const args: ExprNode[] = [];

        if (this.peek().kind !== 'rparen') {
          args.push(this.expr());
          while (this.peek().kind === 'comma') {
            this.advance(); // consume ','
            args.push(this.expr());
          }
        }

        this.expect('rparen');
        return func(ident as FuncName, args);
      }
    }

    return this.atom();
  }

  // atom := number | identifier | "(" expr ")"
  private atom(): ExprNode {
    const current = this.peek();

    if (current.kind === 'number') {
      this.advance();
      const str = current.value;
      const value = str.includes('.') ? parseFloat(str) : parseInt(str, 10);
      return num(value);
    }

    if (current.kind === 'identifier') {
      this.advance();
      return variable(current.value);
    }

    if (current.kind === 'lparen') {
      this.advance(); // consume '('
      const inner = this.expr();
      this.expect('rparen');
      return inner;
    }

    if (current.kind === 'eof') {
      throw new ParseError(`Unexpected end of expression`, current.position);
    }

    throw new ParseError(`Unexpected token '${current.value}' (${current.kind})`, current.position);
  }

  /**
   * Tokens that can start an implicit multiplication operand.
   * Note: bare numbers are excluded — "1 2" is a syntax error, not mul([1,2]).
   * Implicit mul triggers only on identifier (3x) or lparen (2(x+1)).
   */
  private canStartAtom(token: Token): boolean {
    return token.kind === 'identifier' || token.kind === 'lparen';
  }
}

// ---------------------------------------------------------------------------
// Free-variable extraction
// ---------------------------------------------------------------------------

function collectVarNames(node: ExprNode, out: Set<string>): void {
  switch (node.kind) {
    case 'num':
      break;
    case 'var':
      out.add(node.name);
      break;
    case 'add':
    case 'mul':
      for (const child of node.children) {
        collectVarNames(child, out);
      }
      break;
    case 'pow':
      collectVarNames(node.base, out);
      collectVarNames(node.exp, out);
      break;
    case 'func':
      for (const arg of node.args) {
        collectVarNames(arg, out);
      }
      break;
  }
}

function extractVariables(node: ExprNode): string[] {
  const all = new Set<string>();
  collectVarNames(node, all);
  // Remove constant names (pi, e, tau, phi are not free variables)
  for (const constName of Object.keys(CONSTANTS)) {
    all.delete(constName);
  }
  return Array.from(all).sort();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function parse(src: string): ParseResult {
  const parser = new Parser(src);
  const node = parser.parse();
  const variables = extractVariables(node);
  return { node, variables, source: src };
}
